/**
 * Global Error Handler Service
 *
 * Catches all unhandled errors in the application and logs them properly.
 * Integrates with logging infrastructure for better debugging.
 */

import { ErrorHandler, Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AnalyticsService } from '@core/services/analytics';
import { Logger } from '../utils/logger.utils';

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  private router = inject(Router);
  private analytics = inject(AnalyticsService);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  handleError(error: Error | any): void {
    // Errors thrown by browser extensions (e.g. Angular DevTools probing a
    // prod build) are not app bugs — drop them so they don't pollute error
    // tracking or GA4.
    if (this.isExtensionError(error)) {
      return;
    }

    // Extract useful information
    const errorInfo = this.extractErrorInfo(error);

    // Log to our structured logging system
    Logger.error('Uncaught error in application', error, {
      component: 'GlobalErrorHandler',
      url: typeof window !== 'undefined' ? window.location.href : 'unknown',
      route: this.router.url,
      ...errorInfo,
    });

    // Track in GA4 — wrapped to prevent recursive error loops
    try {
      // `error.code` is a string for Firebase Auth errors but a number for
      // Firestore (OK=0, CANCELLED=1, …). Guard before calling .startsWith,
      // and use explicit type checks so a legitimate zero value (e.g.
      // HttpErrorResponse.status === 0 on network/CORS failure, or a
      // numeric code of 0) isn't falsy-misclassified as 'runtime'.
      const code = error?.code;
      const status = error?.status;
      const errorType =
        typeof code === 'string' && code.startsWith('auth/')
          ? 'auth'
          : typeof code === 'string' || typeof code === 'number'
            ? 'firestore'
            : typeof status === 'number'
              ? 'http'
              : 'runtime';
      const message = error?.message || String(error) || 'Unknown error';
      this.analytics.trackError(errorType, message);
    } catch {
      // Swallow analytics errors to avoid infinite loops
    }

    // Re-throw in development for console visibility
    if (!this.isProduction()) {
      console.error('🚨 Uncaught Error:', error);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private isExtensionError(error: any): boolean {
    // zone.js delivers unhandled promise rejections wrapped in an object
    // whose `rejection` property holds the original error.
    const stack = error?.rejection?.stack ?? error?.stack;
    if (typeof stack !== 'string') {
      return false;
    }
    // Only the topmost frame decides where the error originated — an app
    // error whose stack merely passes through an extension frame (e.g. a
    // monkey-patched API) must still be tracked. Frame lines are V8-style
    // ("    at fn (url)") or Firefox/Safari-style ("fn@url").
    const firstFrame = stack.split('\n').find((line) => /^\s+at\s|@\S+:\/\//.test(line));
    if (firstFrame === undefined) {
      return false;
    }
    return /(?:chrome|moz|safari-web)-extension:\/\//.test(firstFrame);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private extractErrorInfo(error: any): Record<string, any> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const info: Record<string, any> = {};

    // Firestore errors
    if (error?.code) {
      info['firestoreCode'] = error.code;
      info['firestoreMessage'] = error.message;
    }

    // HTTP errors
    if (error?.status) {
      info['httpStatus'] = error.status;
      info['httpStatusText'] = error.statusText;
    }

    // Firebase Auth errors — `code` may be a number on Firestore/HTTP-style
    // errors, so guard the type before calling string methods.
    if (typeof error?.code === 'string' && error.code.startsWith('auth/')) {
      info['authErrorCode'] = error.code;
      info['authErrorMessage'] = error.message;
    }

    // Angular errors
    if (error?.ngDebugContext) {
      info['angularComponent'] = error.ngDebugContext?.component?.constructor?.name;
      info['angularView'] = error.ngDebugContext?.view?.def?.componentProvider?.componentType?.name;
    }

    // Stack trace
    if (error?.stack) {
      info['stackTrace'] = error.stack.split('\n').slice(0, 5).join('\n'); // First 5 lines
    }

    return info;
  }

  private isProduction(): boolean {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return typeof window !== 'undefined' && (window as any).__production__ === true;
  }
}
